import React, { useState } from "react"
import { Button, Input, Modal, Select, ToggleFilter } from "uxp/components"
import { IContextProvider } from "../../../uxp"
import jp from 'jsonpath'
import { hasValue, replaceWithData } from "../../../util"
import { WidgetDesignerPillInput } from "../pill-input/PillInput"
import { IWDDesignModeProps } from "../common"
import './QueryBuilder.scss'
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import classNames from "classnames"

export type IOperator = 'eq' | 'ne' | 'gt' | 'gte' | 'lt' | 'lte' | 'in' | 'nin'

export interface IQueryCondition {
    Attribute: string,
    Operator: IOperator,
    Value: any,
}

export interface IQueryConditionGroup {
    IsEither: boolean,
    Conditions: IQueryCondition[],
    Groups: IQueryConditionGroup[]
}

export interface IConditionBuilderProps {
    uxpContext: IContextProvider,
    contextData: any,
    uiProps: any,
    designer: IWDDesignModeProps
    conditions: IQueryConditionGroup,
    onChangeConditions: (conditions: IQueryConditionGroup) => void
}

export const ConditionBuilder: React.FunctionComponent<IConditionBuilderProps> = (props) => {
    const { uxpContext, contextData, uiProps, designer, conditions, onChangeConditions } = props
    return <div className='uxp-wd-condition-builder-container'>
        <ConditionGroup
            uxpContext={uxpContext}
            uiProps={uiProps}
            contextData={contextData}
            designer={designer}
            group={conditions}
            onChangeGroup={g => {
                onChangeConditions(g)
            }}
            index={null}
            isParent
            isEither={true}
            showSuffixPrefix={false}
            onRemoveGroup={() => { }}
        />
    </div>
}


export interface IConditionGroupProps {
    uxpContext: IContextProvider,
    contextData: any,
    uiProps: any,
    designer: IWDDesignModeProps
    group: IQueryConditionGroup,
    index: number,
    onChangeGroup: (group: IQueryConditionGroup) => void
    onRemoveGroup: () => void
    isParent?: boolean
    isEither: boolean,
    showSuffixPrefix: boolean
}

export const ConditionGroup: React.FunctionComponent<IConditionGroupProps> = (props) => {
    let { group, index, onChangeGroup, uxpContext, uiProps, contextData, designer, isParent, onRemoveGroup } = props

    const showSuffixPrefix = (group.Conditions.length + group.Groups.length) > 1


    return <div className={classNames('uxp-wd-condition-builder-groups-container')}>
        {props.showSuffixPrefix &&
            <div className="uxp-wd-condition-prefix">
                {props.isEither ? 'Or' : 'And'}
            </div>
        }
        <div className="uxp-wd-condition-group">
            <div className="uxp-wd-condition-group-header">
                <ToggleFilter
                    value={group.IsEither ? 'or' : 'and'}
                    onChange={s => {
                        jp.apply(group, '$.IsEither', () => (s == 'or'))
                        console.log('final', group)
                        onChangeGroup(group)
                    }}
                    options={[
                        { label: 'Either', value: 'or' },
                        { label: 'All', value: 'and' },
                    ]}
                />

                <div className="actions">
                    <div className="icon-button"
                        onClick={() => {
                            group.Conditions.push({ Attribute: '', Operator: 'eq', Value: '' })
                            onChangeGroup(group)
                        }}
                    >
                        <FontAwesomeIcon icon={['fas', 'plus']} />
                    </div>
                    <div className="icon-button"
                        onClick={() => {
                            group.Groups.push({ IsEither: true, Conditions: [], Groups: [] })
                            onChangeGroup(group)
                        }}
                    >
                        <FontAwesomeIcon icon={['fas', 'plus-square']} />
                    </div>
                </div>
            </div>

            <div className="uxp-wd-condition-group-body">
                {
                    group?.Conditions?.map((condition, i) => {
                        return <Condition
                            uxpContext={uxpContext}
                            contextData={contextData}
                            uiProps={uiProps}
                            designer={designer}
                            condition={condition}
                            index={i}
                            onChangeCondition={(updated) => {
                                group.Conditions[i] = updated
                                onChangeGroup(group)
                            }}
                            onRemoveCondition={() => {
                                group.Conditions.splice(i, 1)
                                onChangeGroup(group)
                            }}
                            isEither={group.IsEither}
                            showSuffixPrefix={showSuffixPrefix}
                        />
                    })
                }
                {
                    group?.Groups?.map((cGroup, i) => {
                        return <ConditionGroup
                            uxpContext={uxpContext}
                            contextData={contextData}
                            uiProps={uiProps}
                            designer={designer}
                            group={cGroup}
                            index={i}
                            onChangeGroup={(updated) => {
                                group.Groups[i] = updated
                                onChangeGroup(group)
                            }}
                            onRemoveGroup={() => {
                                group.Groups.splice(i, 1)
                                onChangeGroup(group)
                            }}
                            isEither={group.IsEither}
                            showSuffixPrefix={showSuffixPrefix}
                        />
                    })
                }
            </div>
        </div>

        {!isParent &&
            <div className="uxp-wd-condition-suffix group">

                <div className='icon-button' onClick={onRemoveGroup} >
                    <FontAwesomeIcon icon={['fas', 'minus']} />
                </div>

            </div>
        }

    </div>
}

export interface IConditionProps {
    uxpContext: IContextProvider,
    contextData: any,
    uiProps: any,
    designer: IWDDesignModeProps
    condition: IQueryCondition,
    index: number,
    onChangeCondition: (condition: IQueryCondition) => void
    onRemoveCondition: () => void
    isEither: boolean,
    showSuffixPrefix: boolean
}

export const Condition: React.FunctionComponent<IConditionProps> = (props) => {
    let { condition, index, onChangeCondition, isEither, uxpContext, uiProps, contextData, designer, showSuffixPrefix, onRemoveCondition } = props

    return <div className="uxp-wd-condition-container">

        {showSuffixPrefix &&
            <div className="uxp-wd-condition-prefix">
                {isEither ? 'Or' : 'And'}
            </div>
        }
        <div className="uxp-wd-condition">
            <WidgetDesignerPillInput
                value={condition.Attribute}
                onChange={s => {
                    jp.apply(condition, '$.Attribute', () => s)
                    onChangeCondition(condition)
                }}
                contextData={contextData}
            />

            <Select
                selected={condition.Operator}
                onChange={(s) => {
                    jp.apply(condition, '$.Operator', () => s)
                    onChangeCondition(condition)
                }}
                options={[
                    { label: 'Equals', value: 'eq' },
                    { label: 'Not Equals', value: 'ne' },
                    { label: 'Greater than', value: 'gt' },
                    { label: 'Greater than or quals', value: 'gte' },
                    { label: 'Less than', value: 'lt' },
                    { label: 'Less than or quals', value: 'lte' },
                    { label: 'Is one of', value: 'in' },
                    { label: 'Is not one of', value: 'nin' },
                ]}
            />

            <WidgetDesignerPillInput
                value={condition.Value}
                onChange={s => {
                    jp.apply(condition, '$.Value', () => s)
                    onChangeCondition(condition)
                }}
                contextData={contextData}
            />
        </div>

        <div className="uxp-wd-condition-suffix">

            <div className='icon-button' onClick={onRemoveCondition} >
                <FontAwesomeIcon icon={['fas', 'minus']} />
            </div>

        </div>

    </div>
}

export const QueryBuilderModal: React.FunctionComponent<IConditionBuilderProps & { title?: string, }> = (props) => {
    const { title, ...rest } = props

    const [show, setShow] = useState(false)

    const hasConditions = rest?.conditions?.Groups?.length > 0 || rest?.conditions?.Conditions?.length > 0

    return <div className="uxp-conditions-editor-modal-container">
        <Button
            title={hasConditions ? 'Edit Conditions' : 'Add Conditions'}
            onClick={() => setShow(true)}
            active={hasConditions}
            className="uxp-conditions-editor-button"
        />

        <Modal
            show={show}
            onClose={() => setShow(false)}
            title={title || 'Condition Builder'}
            className="uxp-conditions-editor-modal"
        >
            <ConditionBuilder {...rest} />
        </Modal>
    </div>
}

export function evaluateConditions(conditions: IQueryConditionGroup, contextData: any): boolean {

    const { IsEither, Conditions, Groups } = conditions

    if ((Conditions.length + Groups.length) == 0) return true

    let isTrue = IsEither ? false : true
    for (const c of Conditions) {
        const isConditionTrue = evaluateCondition(c, contextData)
        if (IsEither && isConditionTrue) { isTrue = true; break; }
        if (!IsEither && !isConditionTrue) { isTrue = false; break; }
    }

    for (const g of Groups) {
        const isGroupTrue = evaluateConditions(g, contextData)
        if (IsEither && isGroupTrue) { isTrue = true; break; }
        if (!IsEither && !isGroupTrue) { isTrue = false; break; }
    }
    return isTrue
}

export function evaluateCondition(condition: IQueryCondition, contextData: any): boolean {
    const { Attribute, Operator, Value } = condition
    const parsedAttribute = replaceWithData(Attribute?.trim(), contextData)
    const parsedValue = Operator == 'in' ? (Value as string)?.trim()?.split(",").filter(s => !!s).map(s => replaceWithData(s, contextData)) : replaceWithData(Value?.trim(), contextData)


    switch (Operator) {
        case 'eq':
            return (parsedAttribute == parsedValue)
        case 'ne':
            return (parsedAttribute != parsedValue)
        case 'gt':
            return (parsedAttribute > parsedValue)
        case 'gte':
            return (parsedAttribute >= parsedValue)
        case 'lt':
            return (parsedAttribute < parsedValue)
        case 'lte':
            return (parsedAttribute <= parsedValue)
        case 'in':
            return (parsedValue as string[]).includes(parsedAttribute)
        case 'nin':
            return !(parsedValue as string[]).includes(parsedAttribute)
        default:
            return true
    }
}